// "What do I owe, and by when?" — the question a buyer's home page never answered. Lists every amount
// that's due now or overdue across all their orders, the next date something falls due, and a Pay
// button per amount (opens the secure payment page).
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchWorklist, generateLink } from '../api/payments.js';
import { fmtMoney } from '../lib/matrix.js';
import { installmentTiming } from '../lib/labels.js';

const sumBy = (items) => {
  const out = {};
  for (const i of items) out[i.currency] = (out[i.currency] || 0) + Number(i.amount || 0);
  return Object.entries(out);
};
const shortLabel = (label) => String(label || 'Payment').replace(/\s*\(.*\)\s*$/, '');

/** companyId: a staff preview of one client's amounts (their own page passes nothing). */
export default function WhatYouOwe({ companyId = null }) {
  const [items, setItems] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const inFlight = useRef(false);

  useEffect(() => {
    fetchWorklist({ pageSize: 200, ...(companyId ? { companyId } : {}) })
      .then((r) => {
        const all = r.items || [];
        setItems(all.filter((i) => i.bucket === 'overdue' || i.bucket === 'due'));
        setUpcoming(all.filter((i) => i.bucket === 'awaiting_arrival' || (i.bucket === 'due' && i.dueDate)));
      })
      .catch(() => setItems([]));
  }, [companyId]);

  if (items === null) return null;

  const pay = async (i) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(i.id);
    setError('');
    try {
      let url = i.linkUrl;
      if (!url) url = (await generateLink(i.id)).installment?.linkUrl;
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Could not open the payment page — please try again.');
    } finally {
      inFlight.current = false;
      setBusy('');
    }
  };

  const overdue = items.filter((i) => i.bucket === 'overdue');
  const nextDated = upcoming.filter((i) => i.dueDate).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))[0];

  if (!items.length) {
    return (
      <div className="mb-4 rounded-lg border border-success/30 bg-success-light/50 p-4 text-sm text-text-primary">
        <strong>You’re all paid up.</strong>
        {nextDated ? ` Next payment: ${shortLabel(nextDated.label)} for ${nextDated.orderNumber}, ${fmtMoney(nextDated.amount, nextDated.currency)} — ${installmentTiming(nextDated).toLowerCase()}.` : ' Nothing is due right now.'}
      </div>
    );
  }

  return (
    <div className={`mb-4 rounded-lg border p-4 shadow-sm ${overdue.length ? 'border-error/40 bg-error-light/40' : 'border-accent/40 bg-accent-light/40'}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-text-primary">{overdue.length ? 'Payment overdue' : 'Ready to pay'}</h2>
        <div className="text-lg font-semibold tabular-nums text-text-primary">
          {sumBy(items).map(([c, a]) => fmtMoney(a, c)).join(' · ')} <span className="text-sm font-normal text-text-secondary">due now</span>
        </div>
      </div>
      <ul className="mt-3 divide-y divide-line/70 rounded-md border border-line bg-surface">
        {items.map((i) => (
          <li key={i.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 text-sm">
            <div className="min-w-0">
              <Link to={`/orders/${i.orderId}`} className="font-medium text-text-primary hover:text-accent hover:underline">
                {i.orderNumber}
              </Link>
              <span className="text-text-secondary"> · {shortLabel(i.label)}</span>
              <div className={`text-xs ${i.bucket === 'overdue' ? 'font-medium text-error' : 'text-text-muted'}`}>{installmentTiming(i)}</div>
            </div>
            <button
              onClick={() => pay(i)}
              disabled={busy === i.id}
              className="rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-50"
            >
              {busy === i.id ? 'Opening…' : `Pay ${fmtMoney(i.amount, i.currency)}`}
            </button>
          </li>
        ))}
      </ul>
      {error && <p className="mt-2 text-xs text-error">{error}</p>}
      <p className="mt-2 text-[11px] text-text-muted">Paying by bank transfer instead? Use the order number as the reference — details are on each invoice.</p>
    </div>
  );
}
